import { useTranslation } from "react-i18next";
import { Controller, Control } from "react-hook-form";
import { motion } from "framer-motion";
import { RawMetricCode } from "types/assessmentTypes";
import { ExportType } from "types/companyTypes";
import { enumToOptions } from "services/helper";
import { useMemo, useCallback } from "react";
import {
  METRICS_WITH_UNIT,
  QuantitativeFormDataExtended,
} from "./QuantitativeForm";

interface QuantitativeFieldProps {
  code: RawMetricCode;
  index: number;
  control: Control<QuantitativeFormDataExtended>;
  isReadOnly: boolean;
  watchedNominalUnit?: string;
  isFirstUnit?: boolean;
  isSingleValue?: boolean;
  years: number[];
}

export function QuantitativeField({
  code,
  index,
  control,
  isReadOnly,
  watchedNominalUnit,
  isFirstUnit = false,
  isSingleValue = false,
  years,
}: QuantitativeFieldProps) {
  const { t } = useTranslation();

  const UNIT_OPTIONS = useMemo(
    () => [
      { value: "TON", label: t("unitTypes.TON") },
      { value: "PIECE", label: t("unitTypes.PIECE") },
      { value: "WORKING_HOUR", label: t("unitTypes.WORKING_HOUR") },
      { value: "SHIFT", label: t("unitTypes.SHIFT") },
      { value: "KILOGRAM", label: t("unitTypes.KILOGRAM") },
      { value: "LITER", label: t("unitTypes.LITER") },
      { value: "MILLION", label: t("unitTypes.MILLION") },
      { value: "METER", label: t("unitTypes.METER") },
      { value: "PERSON", label: t("unitTypes.PERSON") },
      { value: "NUMBER", label: t("unitTypes.NUMBER") },
    ],
    [t]
  );

  const EXPORT_UNIT_OPTIONS = useMemo(
    () => enumToOptions(ExportType, t, "exportTypes"),
    [t]
  );

  const UNIT_MAP = useMemo(
    () =>
      UNIT_OPTIONS.reduce(
        (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
        {} as Record<string, string>
      ),
    [UNIT_OPTIONS]
  );

  const getUnitType = useCallback(
    (unitKey: string) => {
      return t(`assessment.detail.quantitative.units.${unitKey}`);
    },
    [t]
  );

  const getMetricLabel = useCallback(
    (code: RawMetricCode, index: number) => {
      return `${index + 1}. ${t(
        `assessment.detail.quantitative.fields.${code}`
      )}`;
    },
    [t]
  );

  const getMetricUnitKey = useCallback((code: RawMetricCode): string => {
    const unitMap: Record<RawMetricCode, string> = {
      NET_SALES: "MILLION",
      OPERATING_PROFIT: "MILLION",
      NET_PROFIT: "MILLION",
      TOTAL_ASSETS: "MILLION",
      NOMINAL_CAPACITY: "UNIT",
      ACTUAL_PRODUCTION: "UNIT",
      INVENTORY: "MILLION",
      ACCOUNTS_RECEIVABLE: "MILLION",
      ACCOUNTS_PAYABLE: "MILLION",
      LOGISTICS_COST: "MILLION",
      RETURN_VALUE: "MILLION",
      RND_COST: "MILLION",
      TOTAL_ENERGY_COST: "MILLION",
      RENEWABLE_ENERGY_COST: "MILLION",
      VOLUNTARY_EXIT_EMPLOYEES: "PERSON",
      AVG_EMPLOYEES: "PERSON",
      CURRENT_EMPLOYEES: "PERSON",
      ACTIVE_PATENTS: "NUMBER",
      NEW_PATENT_REQUESTS: "NUMBER",
      NET_SALES_KNOWLEDGE_AND_TECHNOLOGIC: "MILLION",
      EXPORT_AMOUNT: "MILLION",
      EXPORT_VOLUME: "UNIT",
    };
    return unitMap[code] || "MILLION";
  }, []);

  const needsUnitSelection = useCallback(
    (code: RawMetricCode) => METRICS_WITH_UNIT.includes(code),
    []
  );

  const isCustomUnit = useCallback(
    (unit: string): boolean => {
      return !!unit && !UNIT_OPTIONS.some((option) => option.value === unit);
    },
    [UNIT_OPTIONS]
  );

  const isCustomExportUnit = useCallback(
    (unit: string): boolean => {
      return (
        !!unit && !EXPORT_UNIT_OPTIONS.some((option) => option.value === unit)
      );
    },
    [EXPORT_UNIT_OPTIONS]
  );

  const getUnitLabel = useCallback(
    (unitValue: string) => {
      return UNIT_MAP[unitValue] || unitValue;
    },
    [UNIT_MAP]
  );

  const handleNumberInput = useCallback(
    (value: string, onChange: (value: number | undefined) => void) => {
      if (value === "") {
        onChange(undefined);
        return;
      }
      const numericValue = value.replace(/[^0-9.]/g, "");
      const parts = numericValue.split(".");
      if (parts.length > 2) return;
      const parsed = parseFloat(numericValue);
      if (!isNaN(parsed)) {
        onChange(parsed);
      }
    },
    []
  );

  const metricLabel = getMetricLabel(code, index);
  const unitKey = getMetricUnitKey(code);
  const unitType = getUnitType(unitKey);
  const needsUnit = needsUnitSelection(code);
  const isExportVolume = code === "EXPORT_VOLUME";

  if (isSingleValue) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.015 }}
        className={`grid grid-cols-6 gap-2 rounded-lg p-1.5 transition-colors ${
          index % 2 === 0
            ? "bg-white dark:bg-navy-800"
            : "bg-gray-50/50 dark:bg-navy-700/50"
        } hover:bg-brand-50/50 dark:hover:bg-brand-900/10 ${
          isReadOnly ? "opacity-70" : ""
        }`}
      >
        <div className="col-span-2 flex items-center px-2">
          <span className="text-sm font-bold text-navy-900 dark:text-white">
            {metricLabel}
            {isSingleValue && (
              <span
                className="ms-1 text-sm font-semibold text-red-500"
                title={t("assessment.detail.quantitative.singleValueRequired")}
              >
                *
              </span>
            )}
          </span>
        </div>

        <div className="col-span-1 flex items-center justify-center px-2">
          <span className="text-sm text-gray-700 dark:text-gray-400">
            {unitType}
          </span>
        </div>

        <div className="col-span-3 px-1">
          <Controller
            name={`${code}.value` as const}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => {
              const displayValue = value ?? "";
              const hasValue = displayValue !== "";

              return (
                <input
                  {...rest}
                  type="text"
                  inputMode="decimal"
                  value={displayValue}
                  onChange={(e) => {
                    handleNumberInput(e.target.value, onChange);
                  }}
                  disabled={isReadOnly}
                  aria-required={!isReadOnly}
                  className={`w-full rounded-lg border px-2 py-1.5 text-center text-sm transition-all focus:outline-none focus:ring-1 ${
                    hasValue
                      ? "border-green-400 bg-white focus:border-green-500 focus:ring-green-500 dark:border-green-700 dark:bg-navy-700 dark:text-white"
                      : "border-gray-200 bg-white focus:border-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
                  } ${isReadOnly ? "cursor-not-allowed opacity-60" : ""}`}
                  placeholder="0"
                />
              );
            }}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.015 }}
      className={`grid grid-cols-6 gap-2 rounded-lg p-1.5 transition-colors ${
        index % 2 === 0
          ? "bg-white dark:bg-navy-800"
          : "bg-gray-50/50 dark:bg-navy-700/50"
      } hover:bg-brand-50/50 dark:hover:bg-brand-900/10 ${
        isReadOnly ? "opacity-70" : ""
      }`}
    >
      <div className="col-span-2 flex items-center px-2">
        <span className="text-sm font-bold text-navy-900 dark:text-white">
          {metricLabel}
        </span>
      </div>

      <div className="col-span-1 flex items-center justify-center px-2">
        {needsUnit ? (
          isExportVolume ? (
            <Controller
              name={`${code}.unit` as const}
              control={control}
              render={({ field: { onChange, value } }) => {
                const currentValue = value as string;
                const isCustom = isCustomExportUnit(currentValue);
                const selectValue =
                  currentValue && !isCustom ? currentValue : "";

                return (
                  <div className="w-full">
                    <div className="flex items-center justify-center gap-2">
                      <select
                        value={selectValue}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          onChange(newValue);
                        }}
                        disabled={isReadOnly}
                        aria-required={!isReadOnly}
                        className={`w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white ${
                          isReadOnly ? "cursor-not-allowed opacity-60" : ""
                        }`}
                      >
                        <option value="">{t("actions.choose")}</option>
                        {EXPORT_UNIT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <span
                        className="text-sm font-semibold text-red-500"
                        title={t(
                          "assessment.detail.quantitative.exportUnitRequired"
                        )}
                      >
                        *
                      </span>
                    </div>
                    {(selectValue === "OTHER" || isCustom) && (
                      <input
                        type="text"
                        value={
                          isCustom && currentValue !== "OTHER"
                            ? currentValue
                            : ""
                        }
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        disabled={isReadOnly}
                        aria-label={t(
                          "assessment.detail.quantitative.exportUnitCustomLabel"
                        )}
                        placeholder={t(
                          "assessment.detail.quantitative.customUnitPlaceholder"
                        )}
                        className={`mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white ${
                          isReadOnly ? "cursor-not-allowed opacity-60" : ""
                        }`}
                      />
                    )}
                  </div>
                );
              }}
            />
          ) : isFirstUnit ? (
            <Controller
              name={`${code}.unit` as const}
              control={control}
              render={({ field: { onChange, value } }) => {
                const currentValue = value as string;
                const isCustom = isCustomUnit(currentValue);
                const selectValue = isCustom ? "OTHER" : currentValue || "TON";

                return (
                  <div className="w-full">
                    <select
                      value={selectValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        onChange(newValue);
                      }}
                      disabled={isReadOnly}
                      className={`w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white ${
                        isReadOnly ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    >
                      {UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                      <option value="OTHER">{t("unitTypes.OTHER")}</option>
                    </select>
                    {(selectValue === "OTHER" || isCustom) && (
                      <input
                        type="text"
                        value={
                          isCustom && currentValue !== "OTHER"
                            ? currentValue
                            : ""
                        }
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        disabled={isReadOnly}
                        placeholder={t(
                          "assessment.detail.quantitative.customUnitPlaceholder"
                        )}
                        className={`mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm transition-all focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white ${
                          isReadOnly ? "cursor-not-allowed opacity-60" : ""
                        }`}
                      />
                    )}
                  </div>
                );
              }}
            />
          ) : (
            <div className="flex w-full flex-col items-center gap-1">
              <span className="text-sm text-gray-700 dark:text-gray-400">
                {watchedNominalUnit && watchedNominalUnit !== "OTHER"
                  ? getUnitLabel(watchedNominalUnit)
                  : t("actions.choose")}
              </span>
            </div>
          )
        ) : (
          <span className="text-sm text-gray-700 dark:text-gray-400">
            {unitType}
          </span>
        )}
      </div>

      {years.map((year, index) => (
        <div key={year} className="col-span-1 px-1">
          <Controller
            name={`${code}.${year}` as const}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => {
              const displayValue = value ?? "";
              const hasValue = displayValue !== "";

              return (
                <input
                  {...rest}
                  type="text"
                  inputMode="decimal"
                  value={displayValue}
                  onChange={(e) => {
                    handleNumberInput(e.target.value, onChange);
                  }}
                  disabled={isReadOnly}
                  aria-required={year === years[0] && !isReadOnly}
                  className={`w-full rounded-lg border px-2 py-1.5 text-center text-sm transition-all focus:outline-none focus:ring-1 ${
                    hasValue
                      ? "border-green-400 bg-white focus:border-green-500 focus:ring-green-500 dark:border-green-700 dark:bg-navy-700 dark:text-white"
                      : "border-gray-200 bg-white focus:border-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
                  } ${isReadOnly ? "cursor-not-allowed opacity-60" : ""}`}
                  placeholder="0"
                />
              );
            }}
          />
        </div>
      ))}
    </motion.div>
  );
}
